/**
 * URL 网页导入 — 抓取 HTML 后提取正文
 * 对齐 features.md 1.5：URL 网页导入
 *
 * 抓取策略（按优先级）：
 *   1. Web 端：通过注入的 fetchFn 调后端代理接口（绕过 CORS）
 *   2. Tauri 端：直接 fetch（无 CORS 限制）
 *   3. 无 fetchFn 时降级：直连 → corsproxy.io → 报错
 */

import type { Block } from '@hump/shared-types';
import type { Parser, ParseResult } from './types';
import { createBlock } from './block-builder';

/**
 * 可注入的网页抓取函数：接收 URL，返回 HTML 字符串。
 * Web 端由 doc.ts 注入 bffClient.import.fetchUrl（走后端代理）。
 */
export type UrlFetchFn = (url: string) => Promise<string>;

export class UrlParser implements Parser {
  readonly sourceType = 'url' as const;

  constructor(private fetchFn?: UrlFetchFn) {}

  async parse(input: string): Promise<ParseResult> {
    const url = input.trim();

    const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

    let html: string;
    if (isTauri) {
      // Tauri 端：直接 fetch（Rust 端无 CORS 限制）
      const res = await fetch(url);
      html = await res.text();
    } else if (this.fetchFn) {
      // Web 端：通过注入的后端代理抓取
      html = await this.fetchFn(url);
    } else {
      // 无注入函数（测试/独立调用）：降级直连 + 公共代理
      html = await this.fetchFallback(url);
    }

    // 用 DOMParser 解析 HTML
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // 提取标题
    const title = doc.querySelector('title')?.textContent?.trim() || url;

    // 简易正文提取：移除 script/style/nav/header/footer
    doc.querySelectorAll('script, style, nav, header, footer, aside, iframe').forEach((el) => el.remove());

    // 找正文容器（常见 class）
    const article = doc.querySelector('article') || doc.querySelector('main') || doc.querySelector('.content') || doc.body;

    const blocks: Block[] = [];
    let order = 0;

    const walker = doc.createTreeWalker(article, NodeFilter.SHOW_ELEMENT, {
      acceptNode(node: Element) {
        const tag = node.tagName.toLowerCase();
        if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'p' || tag === 'blockquote' || tag === 'pre') {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      },
    });

    const elements: Element[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      elements.push(node as Element);
    }

    for (const el of elements) {
      const text = (el.textContent || '').trim();
      if (!text || text.length < 2) continue;

      const tag = el.tagName.toLowerCase();
      let type: Block['type'] = 'paragraph';
      let level: number | undefined;

      if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
        type = 'heading';
        level = tag === 'h1' ? 1 : tag === 'h2' ? 2 : 3;
      } else if (tag === 'blockquote') {
        type = 'quote';
      } else if (tag === 'pre') {
        type = 'code';
      }

      blocks.push(createBlock({ docId: '', order: order++, type, level, original: text }));
    }

    return { blocks, meta: { title, sourceType: 'url', lang: 'en' } };
  }

  /** 降级抓取：直连 → 公共代理（仅无 fetchFn 时用，如测试） */
  private async fetchFallback(url: string): Promise<string> {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.text();
    } catch {
      try {
        const proxyUrl = `https://corsproxy.io/?url=${encodeURIComponent(url)}`;
        const proxyRes = await fetch(proxyUrl);
        if (!proxyRes.ok) throw new Error(`HTTP ${proxyRes.status}`);
        return await proxyRes.text();
      } catch {
        throw new Error(
          'URL 导入失败。请检查链接是否可访问，或稍后重试。'
        );
      }
    }
  }
}
